import { useState } from 'react';
import useSWR from 'swr';
import { get } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { PlaceOrderDialog } from '@/components/domain/PlaceOrderDialog';
import { QueueLoadError } from '@/components/domain/QueueLoadError';
import { orderingQueue, productNameOf, type AnySub } from '@/lib/ordering';
import { tiersFromSubmission } from '@/lib/prize-tiers';
import {
  fetchAllSubmissions, submissionsPageUrl, truncationNotice,
  type LoadedSubmissions,
} from '@/lib/submission-pages';

type PrizePayload = { products: Array<{ id: string; shortName?: string; displayName?: string }> };

// Last of the rep queues still reading a single fixed page. 453 final_approved
// rows against a 500 limit left 47 of headroom, and the row dropped first is
// the OLDEST-created — which, now that the server orders this queue by
// finalApprovedAt, is not the least urgent one. Page to the end like Orders and
// Submissions already do, and say so when the list is provably short.
const APPROVED_QUEUE_URL = submissionsPageUrl({ status: 'final_approved' });

// Place Orders — final_approved fundraisers ready to order.
// Port of admin-dashboard's Ordering tab.
export default function Ordering() {
  const [q, setQ] = useState('');
  const [orderSub, setOrderSub] = useState<AnySub | null>(null);
  // The key stays the first-page URL so refreshSubmission()'s
  // `/api/submissions?` prefix match still revalidates this tab after a mutation.
  const { data, error, isLoading, mutate } = useSWR<LoadedSubmissions>(
    APPROVED_QUEUE_URL,
    (url: string) => fetchAllSubmissions(url, get),
  );
  const { data: prize } = useSWR<PrizePayload>('/api/prize-products', get);
  const names = (id?: string) => productNameOf(prize?.products || [], id);
  const incomplete = data ? truncationNotice(data) : null;

  const list = data?.submissions || [];
  // Most-recently-APPROVED first. The server sorts this queue by
  // finalApprovedAt now, but its degraded path falls back to createdAt when the
  // index is missing or still building. Because the list above is paged to the
  // END, re-sorting here keeps the order right in that case too rather than
  // showing a silently mis-ordered queue. See orderingQueue() in lib/ordering.ts.
  const matched = list.filter((s) => {
    if (!q.trim()) return true;
    const needle = q.toLowerCase();
    return (
      String(s.fundraiserName || '').toLowerCase().includes(needle) ||
      String(s.id || '').toLowerCase().includes(needle) ||
      String(s.adminName || '').toLowerCase().includes(needle)
    );
  });
  const filtered = orderingQueue(matched);

  return (
    <div className="px-7 py-6">
      <h1 className="text-[20px] font-semibold tracking-tighter">Place Orders</h1>
      <p className="mb-3 text-[12px] text-muted">
        Review and submit orders for closed fundraisers · {filtered.length} ready to order
        {data?.total != null && ` · ${data.total} approved in total`}
      </p>

      {/* Only rendered when the queue is provably NOT whole. Silence here is a
          positive claim: every approved fundraiser is in the table below. */}
      {incomplete && (
        <div className="mb-3 rounded-sm border border-warning/40 bg-warning/10 px-3 py-2 text-[12px] text-text/85">
          {incomplete}
        </div>
      )}

      <div className="mb-3 flex items-center gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search fundraisers ready to order…" className="max-w-[300px]" />
        <Button variant="secondary" size="sm" onClick={() => void mutate()}>Refresh</Button>
      </div>

      <div className="rounded-md bg-surface shadow-card">
        {isLoading ? (
          <div className="space-y-2 p-3">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
          </div>
        ) : error ? (
          <QueueLoadError queue="the approved queue" onRetry={() => void mutate()} />
        ) : filtered.length ? (
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="border-b border-border text-left text-[10.5px] uppercase tracking-wide text-subtle">
                <th className="px-3 py-2 font-semibold">Fundraiser</th>
                <th className="px-3 py-2 font-semibold">Admin</th>
                <th className="px-3 py-2 font-semibold">Products</th>
                <th className="px-3 py-2 font-semibold" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id} className="border-b border-border/50 hover:bg-bg/50">
                  <td className="px-3 py-2">
                    <div className="font-medium">{s.fundraiserName || '-'}</div>
                    <div className="font-mono text-[10px] text-subtle">{s.id}</div>
                  </td>
                  <td className="px-3 py-2">{s.adminName || '-'}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-col gap-1">
                      {tiersFromSubmission(s).map((t) => (
                        <span key={t.index} className="w-fit rounded-sm bg-primary/10 px-1.5 py-0.5 text-[11px] text-primary">
                          {names(t.product || undefined)}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" onClick={() => setOrderSub(s)}>Place Order</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-8 text-center text-[12px] text-muted">
            {q ? 'No matching fundraisers.' : 'No fundraisers ready to order — approved fundraisers appear here.'}
          </div>
        )}
      </div>

      <PlaceOrderDialog
        sub={orderSub}
        open={!!orderSub}
        onClose={() => setOrderSub(null)}
        onPlaced={() => void mutate()}
        productNames={names}
      />
    </div>
  );
}
